import React from 'react';
import { Card, CardContent } from '../ui/card';
import { TrendingUp, Sparkles } from 'lucide-react';

/**
 * SEO-oriented content expansion section for service pages.
 * Renders two content blocks:
 *   1. "Our Process" — numbered step-by-step breakdown
 *   2. "What Affects Your Price" — honest pricing factor guidance
 *
 * Props:
 *   processSteps: Array<{title, description}>
 *   pricingFactors: Array<{title, description}>
 *   accentColor: one of "sky" | "green" | "orange" | "purple" | "teal"
 */
const ACCENTS = {
  sky: { bg: 'bg-sky-50', text: 'text-sky-600', ring: 'ring-sky-100', grad: 'from-sky-500 to-sky-700' },
  green: { bg: 'bg-green-50', text: 'text-green-600', ring: 'ring-green-100', grad: 'from-green-500 to-green-700' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600', ring: 'ring-orange-100', grad: 'from-orange-500 to-orange-700' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-600', ring: 'ring-purple-100', grad: 'from-purple-500 to-purple-700' },
  teal: { bg: 'bg-teal-50', text: 'text-teal-600', ring: 'ring-teal-100', grad: 'from-teal-500 to-teal-700' },
};

export const ServiceContentExpansion = ({
  processSteps = [],
  pricingFactors = [],
  accentColor = 'sky',
}) => {
  const c = ACCENTS[accentColor] || ACCENTS.sky;

  return (
    <>
      {/* ---- 1. Our Process ---- */}
      {processSteps.length > 0 && (
        <section className="py-20 bg-white" data-testid="service-process-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <div className={`inline-flex items-center space-x-2 ${c.bg} px-4 py-2 rounded-full mb-4`}>
                <Sparkles className={`h-4 w-4 ${c.text}`} />
                <span className={`${c.text} font-medium text-sm`}>Our Process</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                How We Deliver a Stress-Free Experience
              </h2>
              <p className="text-gray-600">
                Every job follows the same proven five-step workflow. No surprises, no upsells on the day.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {processSteps.map((step, idx) => (
                <div
                  key={idx}
                  className="relative bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`service-process-step-${idx + 1}`}
                >
                  <div
                    className={`absolute -top-4 left-6 h-10 w-10 rounded-full bg-gradient-to-br ${c.grad} text-white font-bold flex items-center justify-center shadow-lg`}
                  >
                    {idx + 1}
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2 mt-3">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---- 2. What Affects Your Price ---- */}
      {pricingFactors.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" data-testid="service-pricing-factors-section">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12 max-w-2xl mx-auto">
              <div className={`inline-flex items-center space-x-2 ${c.bg} px-4 py-2 rounded-full mb-4`}>
                <TrendingUp className={`h-4 w-4 ${c.text}`} />
                <span className={`${c.text} font-medium text-sm`}>Transparent Pricing</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                What Actually Affects Your Price
              </h2>
              <p className="text-gray-600">
                Honest answers about where your dollars go. Use this to compare quotes apples-to-apples.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
              {pricingFactors.map((factor, idx) => (
                <Card
                  key={idx}
                  className="border-0 shadow-md hover:shadow-lg transition-shadow"
                  data-testid={`service-pricing-factor-${idx + 1}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={`h-10 w-10 rounded-lg ${c.bg} flex items-center justify-center flex-shrink-0`}
                      >
                        <span className={`font-bold ${c.text}`}>{idx + 1}</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">{factor.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{factor.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ServiceContentExpansion;
